import { useEffect, useRef, useState } from 'react'
import { I } from './Icons'
import { Working } from './StarTick'
import { shake } from '../lib/motion'
import type { Employee } from '../lib/types'

/** Four-digit employee ID pad, shared by the sign sheet and the Open-the-store screen.
 *  Motion: each digit pops into its box; a wrong ID (or a staff ID on an owner-only pad) shakes the boxes and clears them,
 *  with the message left standing until the next digit. The check itself is unchanged. */
export function IdPad({ employees, ownerOnly, onConfirm, busy, confirmLabel = 'Confirm', busyLabel, dark }: { employees: Employee[]; ownerOnly?: boolean; onConfirm: (e: Employee) => void; busy?: boolean; confirmLabel?: string; busyLabel?: string; dark?: boolean }) {
  const [id, setId] = useState('')
  const [bad, setBad] = useState('')   // the last refusal, shown until the next digit
  const boxes = useRef<HTMLDivElement>(null)
  const match = id.length === 4 ? employees.find((e) => e.employee_id === id && e.active) : undefined
  const ok = !!match && (!ownerOnly || match.rights === 'owner')
  const refusal = id.length < 4 ? '' : !match ? 'No employee with that ID' : (ownerOnly && match.rights !== 'owner') ? `${match.name} is not an owner` : ''
  const msg = ok ? match!.name : refusal || bad
  useEffect(() => {
    if (!refusal) return
    shake(boxes.current)
    const t = window.setTimeout(() => { setBad(refusal); setId('') }, 420)
    return () => window.clearTimeout(t)
  }, [refusal])
  const type = (k: string) => { setBad(''); setId((v) => (v + k).slice(0, 4)) }
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (/^[0-9]$/.test(e.key)) type(e.key)
      else if (e.key === 'Backspace') setId((v) => v.slice(0, -1))
      else if (e.key === 'Enter' && ok && match && !busy) onConfirm(match)
    }
    window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey)
  }, [ok, match, busy, onConfirm])
  return (
    <div className={'idpad' + (dark ? ' idpad--dark' : '')}>
      <div className={'sheet__sub' + (ok ? ' ok' : msg ? ' bad' : '')}>{msg || 'Enter your four-digit employee ID'}</div>
      <div className="idboxes" ref={boxes}>{[0, 1, 2, 3].map((i) => <div key={i} className={'idbox' + (i === id.length ? ' cur' : '')}>{i < id.length ? <span className="m-pop">•</span> : ''}</div>)}</div>
      <div className="pad">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((k) => <button key={k} type="button" className="pad__key" onClick={() => type(k)}>{k}</button>)}
        <button type="button" className="pad__key ghost" onClick={() => { setBad(''); setId('') }}>Clear</button>
        <button type="button" className="pad__key" onClick={() => type('0')}>0</button>
        <button type="button" className="pad__key ghost" aria-label="Delete" onClick={() => setId((v) => v.slice(0, -1))}>{I.del()}</button>
      </div>
      <button type="button" className="cta cta--center sheet__cta" disabled={!ok || busy} onClick={() => match && onConfirm(match)}>{busy ? <Working>{busyLabel ?? 'One moment'}</Working> : confirmLabel}</button>
    </div>
  )
}
